import React, {useContext, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {getShowTrainerMessage, getToken, getUserData, setIntendedUrl, setSearchFilter, setShowTrainerMessage, setToken, setUserData, setUnreadInboxMessagesCount} from '../modules/auth-persistor';
import {getUserProfile} from '../services/auth-service';
import {clearSessionData} from "../modules/session-persistor";
import {useInboxService} from "../services/inbox-service";

const AuthContext = React.createContext();

AuthProvider.propTypes = {
  children: PropTypes.element.isRequired
};

export function AuthProvider ({ children }) {
  const [initializing, setInitializing] = useState(true);
  const [currentUser, _setCurrentUser] = useState(null);
  const [showFitproNotice, _setShowFitproNotice] = useState(null);
  const [unreadMessagesCount, _setUnreadMessagesCount] = useState(null);
  const authenticated = useMemo(() => !!currentUser, [currentUser]);

  const inboxService = useInboxService();

  const initAuth = () => {
    return getToken() && getUserData()?.id
      ? getUserProfile(getUserData().id)
      : Promise.resolve(null);
  };

  //were extending user object with some helpers
  const setCurrentUser = (user = null) => {
    if (user){
      //do some mapping maybe
    }
    _setCurrentUser(user);
  };

  const setShowFitproNotice = (show = null) => {
    _setShowFitproNotice(show);
    setShowTrainerMessage(show);
  };

  const setUnreadMessagesCount = (count = null) => {
    _setUnreadMessagesCount(count);
    setUnreadInboxMessagesCount(count);
  };

  const doLogout = () => {
    setCurrentUser(null);
    setUserData(null);
    setToken(null);
    setSearchFilter(null);
    setIntendedUrl(null);
    setShowFitproNotice(null);
    setUnreadMessagesCount(null);
    clearSessionData();
  };

  function doAppUserLogin(user){
    //set user with roles, mapping and stuff
    setUserData(Object.assign({},user));
    setCurrentUser(Object.assign({},user));

    //we set this once per login.
    let showTrainerMessage = getShowTrainerMessage();
    if (showTrainerMessage===null && user?.trainer){
      setShowFitproNotice(true);
    } else {
      setShowFitproNotice(showTrainerMessage);
    }

    inboxService.getList().then((res) => {
      if (res.unloadedCount){
        setUnreadMessagesCount(res.unloadedCount);
      }
    }).catch((err) => {
      doLogout();
    });
  }

  useEffect(() => {
    initAuth().then((user) => {
      if (!user){
        doLogout();
      } else {
        doAppUserLogin(user);
      }
      setInitializing(false);
    });
    // eslint-disable-next-line
  }, []);

  return (
    <AuthContext.Provider value={{
      initializing,
      authenticated,
      currentUser,
      unreadMessagesCount,
      setUnreadMessagesCount,
      doLogout,
      doAppUserLogin,
      setToken,
      setCurrentUser,
      showFitproNotice,
      setShowFitproNotice,
    }}> { children }
    </AuthContext.Provider>
  );
}

export function useAuth () {
  const {
    initializing,
    authenticated,
    currentUser,
    unreadMessagesCount,
    setUnreadMessagesCount,
    doLogout,
    doAppUserLogin,
    setToken,
    setCurrentUser,
    showFitproNotice,
    setShowFitproNotice,

    ...context
  } = useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }

  return {
    initializing,
    authenticated,
    currentUser,
    unreadMessagesCount,
    setUnreadMessagesCount,
    doLogout,
    doAppUserLogin,
    setToken,
    setCurrentUser,
    showFitproNotice,
    setShowFitproNotice,
  };
}
